import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Fingerprint, Scissors, Sparkles, Ban } from "lucide-react";

export const DifferentiatorSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      className="section-padding bg-white relative overflow-hidden"
      data-testid="differentiator-section"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-emerald-50 to-transparent rounded-full opacity-50 blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-amber-50 to-transparent rounded-full opacity-50 blur-3xl" />

      <div className="lumen-container relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Section header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="font-cinzel text-sm tracking-[0.3em] text-gold-metallic uppercase mb-4 block">
              Le différenciateur
            </span>
            <h2 className="section-title font-cormorant text-text-main mb-6">
              L'IA ne standardise que ceux qui la laissent{" "}
              <span className="italic text-emerald-700">parler comme tout le monde</span>
            </h2>
          </motion.div>

          {/* Verrou ADN highlight */}
          <motion.div
            className="glass-card p-8 md:p-12 mb-12 border-2 border-emerald-100 bg-gradient-to-br from-white to-emerald-50/50"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Fingerprint className="w-10 h-10 text-emerald-600" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-cinzel text-2xl text-emerald-700 tracking-wider mb-3">
                  Le Verrou ADN
                </h3>
                <p className="font-manrope text-lg text-text-main leading-relaxed mb-4">
                  Tu poses <strong>ton vocabulaire</strong>, <strong>ton ton</strong>, <strong>tes interdits</strong>.
                </p>
                <p className="font-cormorant text-xl text-text-muted italic">
                  Résultat : tu fais de la <strong className="text-emerald-700 not-italic">haute couture</strong>, pas du texte en plastique.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Comparison box */}
          <motion.div
            className="grid md:grid-cols-2 gap-8 mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {/* Others */}
            <div className="p-6 bg-stone-50/80 rounded-2xl border border-stone-200">
              <div className="flex items-center gap-3 mb-4">
                <Ban className="w-5 h-5 text-stone-400" />
                <h4 className="font-cormorant text-xl text-text-muted">
                  Les autres "formations IA"
                </h4>
              </div>
              <p className="font-manrope text-text-muted mb-4">
                "Voici des prompts pour faire du texte."
              </p>
              <p className="font-manrope text-sm text-red-500/80 italic">
                Résultat : tu sonnes comme tout le monde, tu t'écœures, tu n'utilises plus l'outil.
              </p>
            </div>

            {/* LUMEN */}
            <div className="p-6 bg-gradient-to-br from-emerald-50/80 to-amber-50/40 rounded-2xl border border-emerald-200">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-5 h-5 text-emerald-600" />
                <h4 className="font-cormorant text-xl text-emerald-700">
                  LUMEN → un protocole
                </h4>
              </div>
              <p className="font-manrope text-text-main mb-4">
                L'IA ne te remplace pas. Elle <strong>amplifie</strong> ta lumière.
              </p>
              <p className="font-manrope text-sm text-emerald-600 italic">
                Tu restes toi. En mieux. En plus visible. En plus impactant(e).
              </p>
            </div>
          </motion.div>

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                number: "01",
                title: "Verrou ADN",
                description: "Tu installes tes mots, ta voix, tes limites. L'IA ne parle pas à ta place. Elle te sert.",
                icon: Fingerprint,
              },
              {
                number: "02",
                title: "Assistants \"10 minutes\"",
                description: "Tu copies-colles → tu obtiens un livrable propre. Tu ajustes 2–3 éléments → tu publies.",
                icon: Scissors,
              },
              {
                number: "03",
                title: "Haute couture",
                description: "Pas plus de blabla. Plus de résultat. Du contenu qui te ressemble et qui convertit.",
                icon: Sparkles,
              },
            ].map((feature, index) => (
              <motion.div
                key={feature.number}
                className="assistant-card group"
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <span className="font-cinzel text-3xl text-emerald-200 font-bold">
                    {feature.number}
                  </span>
                  <div className="icon-circle">
                    <feature.icon className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
                <h3 className="font-cormorant text-2xl text-text-main mb-4">
                  {feature.title}
                </h3>
                <p className="font-manrope text-text-muted leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
